import { api } from "./client";
import type {
  Assessment,
  AssessmentSummary,
  Capability,
  MapJob,
  GuardrailsSummary,
  Observation,
  OutOfScopeEntry,
  OverviewResponse,
  PrefilterResponse,
  RegistryValidation,
  ResolvedGuardrails,
  Review,
  Standard,
  Practice,
  SyncRun,
  Tool,
  TrelicaApp,
  ToolVerdict,
  MapStatus,
} from "./types";

/** Backend liveness, plus whether mapping is available (an Anthropic key is configured)
 * and whether the deployment is read-only. */
export const health = () =>
  api.get<{ ok: boolean; model: string; mapping_available: boolean; read_only: boolean }>("/health");

/** Every capability, with when the capability registry last changed (`last_updated`). */
export const listCapabilities = () =>
  api.getCached<{ last_updated: string | null; capabilities: Capability[] }>("/capabilities");
/** One capability by id. */
export const getCapability = (id: string) => api.get<Capability>(`/capabilities/${encodeURIComponent(id)}`);
/** Moves a capability through its lifecycle (e.g. proposed → active), with an optional note.
 *
 * Edits send the row's own updated_at (base_updated_at): only a change to that row since
 * it was loaded conflicts (409) — see backend domain/freshness.py. */
export const transitionCapability = (id: string, status: string, note: string | undefined, baseUpdatedAt: string) =>
  api.post<Capability>(`/capabilities/${encodeURIComponent(id)}/transition`, {
    status,
    note,
    base_updated_at: baseUpdatedAt,
  });
/** Edits a capability's fields; sends its loaded `updated_at` as base_updated_at, so a
 * change someone else made to that row since it was loaded comes back as a 409. */
export const updateCapability = (id: string, changes: Record<string, unknown>, baseUpdatedAt: string) =>
  api.patch<Capability>(`/capabilities/${encodeURIComponent(id)}`, { ...changes, base_updated_at: baseUpdatedAt });
/** Checks the capability registry (against the out-of-scope entries too) for errors and warnings. */
export const validateCapabilities = () => api.get<RegistryValidation & Record<string, unknown>>("/validate");

/** The enumerated kinds of work that are not Platform work, with the signals that
 * suggest a requirement belongs to one. */
export const listOutOfScope = () => api.get<{ out_of_scope: OutOfScopeEntry[] }>("/out-of-scope");

/** One backend-composed plain-language paragraph per capability, plus how many metadata
 * entries read badly as prose (`flagged`). */
export const getOverview = () => api.getCached<OverviewResponse>("/overview");

/** Every engineering standard, with when the standards catalog last changed and any
 * catalog validation errors/warnings. */
export const listStandards = () =>
  api.getCached<{ last_updated: string | null; standards: Standard[]; errors: string[]; warnings: string[] }>(
    "/standards",
  );
/** Edits a standard; sends its loaded `updated_at` as base_updated_at (409 if that row
 * changed since). */
export const updateStandard = (id: string, changes: Record<string, unknown>, baseUpdatedAt: string) =>
  api.patch<{ standard: Standard; last_updated: string | null }>(`/standards/${encodeURIComponent(id)}`, {
    ...changes,
    base_updated_at: baseUpdatedAt,
  });
/** Checks the standards catalog for errors and warnings. */
export const validateStandards = () => api.get<RegistryValidation>("/validate/standards");

/** The tools catalog (stored fields only — no Trelica call), with when it last changed. */
export const listTools = () => api.getCached<{ last_updated: string | null; tools: Tool[] }>("/tools");
/** One tool by id (stored fields only). */
export const getTool = (id: string) => api.get<Tool>(`/tools/${encodeURIComponent(id)}`);
/** Checks the tools catalog for errors and warnings. */
export const validateTools = () => api.get<RegistryValidation & Record<string, unknown>>("/validate/tools");
/** Links (or relinks) a tool to its Trelica app. Sends the tool's loaded `updated_at` as
 * base_updated_at (409 if it changed since); the backend also copies the linked app's
 * logo onto the tool, best effort. */
export const updateTool = (
  id: string,
  changes: Partial<Pick<Tool, "trelica_app_id" | "note">>,
  baseUpdatedAt: string,
) => api.patch<Tool>(`/tools/${encodeURIComponent(id)}`, { ...changes, base_updated_at: baseUpdatedAt });

/** A tool's live Trelica record (`linked: false` when it has no trelica_app_id yet); a
 * 404 means the stored id no longer matches a Trelica app. Also refreshes the tool's
 * stored logo as a side effect.
 *
 * Trelica is read live, one call per request — ask only for the tool being viewed, and
 * search on submit, never per keystroke (Trelica's limits are ~270 requests/min). */
export const getToolTrelica = (id: string) =>
  api.get<{ linked: boolean; app: TrelicaApp | null }>(`/tools/${encodeURIComponent(id)}/trelica`);
/** Searches Trelica's catalog by name/vendor for linking a tool; the backend requires at
 * least 2 characters. One Trelica call per search. */
export const searchTrelicaApps = (q: string) =>
  api.get<{ apps: TrelicaApp[] }>(`/trelica/apps?q=${encodeURIComponent(q)}`);

/** Confirm or dismiss one model-drafted link on a tool. Confirming makes it count as a
 * curated link ("required by a standard" / "used to deliver this capability"). */
export const changeToolLink = (
  toolId: string,
  action: "confirm" | "dismiss",
  link: "standard" | "capability",
  id: string,
  baseUpdatedAt: string,
) =>
  api.post<Tool>(`/tools/${encodeURIComponent(toolId)}/links/${action}`, { link, id, base_updated_at: baseUpdatedAt });

/** Every practice (the radar's Techniques / Patterns). */
/** Marks a tool or practice reviewed, clearing its "not yet reviewed" flag. */
export const markReviewed = (type: "tool" | "practice", id: string) =>
  api.post<{ reviewed_at: string; reviewed_by: string }>(
    `/${type === "tool" ? "tools" : "practices"}/${encodeURIComponent(id)}/reviewed`,
    {},
  );
export const listPractices = () => api.getCached<{ last_updated: string | null; practices: Practice[] }>("/practices");
export const getPractice = (id: string) => api.get<Practice>(`/practices/${encodeURIComponent(id)}`);
/** Says which standard a practice is the radar's view of; null clears it. */
export const setPracticeSameAs = (practiceId: string, standardId: string | null, baseUpdatedAt: string) =>
  api.post<Practice>(`/practices/${encodeURIComponent(practiceId)}/same-as`, {
    standard: standardId,
    base_updated_at: baseUpdatedAt,
  });
/** Confirm or dismiss a model-drafted link from a practice to a standard. */
export const changePracticeStandard = (
  practiceId: string,
  action: "confirm" | "dismiss",
  standardId: string,
  baseUpdatedAt: string,
) =>
  api.post<Practice>(`/practices/${encodeURIComponent(practiceId)}/standards/${action}`, {
    id: standardId,
    base_updated_at: baseUpdatedAt,
  });

/** When an external system was last batch-synced here; `last_sync` is null if never. */
export const getLastSync = (system: "radar" | "trelica") => api.get<{ last_sync: SyncRun | null }>(`/sync/${system}`);

/** The lexical layer only — instant, no model: capabilities whose vocabulary appears in
 * the text, vetoes, and out-of-scope signals. Recall-limited by design. */
export const prefilter = (text: string) => api.post<PrefilterResponse>("/prefilter", { text });
/** Every capability map, newest first. */
export const listMaps = () => api.get<{ maps: AssessmentSummary[] }>("/maps");
/** One map as the model wrote it — the suggested map, which review never edits. */
export const getMap = (mapId: string) => api.get<Assessment>(`/maps/${encodeURIComponent(mapId)}`);
/** Starts a full map of a product description; the new map gets a UUID once it's written.
 *
 * Returns at once with a pending job (202) — the model call outlasts API Gateway's 30s
 * limit, so it runs in the background. Poll getMapJob until it settles.
 * A 422 means the input failed the server's junk checks; `force` ("Map anyway") skips
 * them, and the model's option to decline the input. */
export const createMap = (text: string, force = false, remap?: { supersedes: string; reason: string }) =>
  api.post<MapJob>("/maps", { text, force, supersedes: remap?.supersedes, remap_reason: remap?.reason });
/** A map job's current state. A job that stopped reporting (e.g. its run timed out) comes
 * back `failed`, so polling always ends. */
export const getMapJob = (id: string) => api.get<MapJob>(`/map-jobs/${encodeURIComponent(id)}`);

/** Whether a map is still current: what changed, what's new since, its relevance check. */
export const getMapStatus = (mapId: string) => api.get<MapStatus>(`/maps/${encodeURIComponent(mapId)}/status`);
/** "Check now": the relevance check for one map, in the background; poll getMapStatus. */
export const checkMap = (mapId: string) =>
  api.post<{ started: boolean }>(`/maps/${encodeURIComponent(mapId)}/check`, {});

/** The Staff+ review of a map. Always the same shape — a map nobody has reviewed yet is
 * `not-started` with every list empty. */
export const getReview = (mapId: string) => api.get<Review>(`/maps/${encodeURIComponent(mapId)}/review`);
/** Appends an attributed, dated note to the review, optionally about one capability. */
export const addReviewNote = (mapId: string, text: string, capability?: string) =>
  api.post<Review>(`/maps/${encodeURIComponent(mapId)}/review/notes`, { text, capability });
/** Records a verdict on one suggested match: `accept`, `reject` (needs a reason — the
 * reason is the correction), or `adjust` (needs a new confidence). Starts the review if
 * it hasn't been; refused once signed off. */
export const reviewMatch = (mapId: string, capability: string, verdict: string, note?: string, confidence?: string) =>
  api.post<Review>(`/maps/${encodeURIComponent(mapId)}/review/matches`, { capability, verdict, note, confidence });
/** Adds a capability the map missed, with the requirement it covers — the highest-value
 * correction there is. */
export const addReviewMatch = (
  mapId: string,
  capability: string,
  confidence: string,
  requirement: string,
  note?: string,
) =>
  api.post<Review>(`/maps/${encodeURIComponent(mapId)}/review/added-matches`, {
    capability,
    confidence,
    requirement,
    note,
  });
/** Records the rule that separates two capabilities the mapper couldn't tell apart; on
 * promotion it becomes a `confused_with` entry on both. */
export const noteContested = (mapId: string, between: string[], note: string, resolution?: string) =>
  api.post<Review>(`/maps/${encodeURIComponent(mapId)}/review/contested`, { between, note, resolution });
/** Records a verdict on one inferred standard: `accept`, `remove`, or `exception`. */
export const reviewStandard = (mapId: string, standard: string, verdict: string, note?: string) =>
  api.post<Review>(`/maps/${encodeURIComponent(mapId)}/review/standards`, { standard, verdict, note });
/** Marks a suggested tool or practice `will-use` or `not-a-fit` — promotion evidence. */
export const reviewTool = (mapId: string, type: "tool" | "practice", id: string, verdict: ToolVerdict, note?: string) =>
  api.post<Review>(`/maps/${encodeURIComponent(mapId)}/review/tools`, { type, id, verdict, note });
/** Adds a standard the mapper failed to infer from the requirement. */
export const addReviewStandard = (mapId: string, standard: string, likelihood: string, because: string) =>
  api.post<Review>(`/maps/${encodeURIComponent(mapId)}/review/added-standards`, { standard, likelihood, because });
/** Signs the review off, freezing it and publishing the map as a delivery brief; refused if the
 * review records nothing. `reopen` reverses that, making it editable again. */
export const signOffReview = (mapId: string, reopen = false) =>
  api.post<Review>(`/maps/${encodeURIComponent(mapId)}/review/sign-off`, { reopen });
/** Turns a signed-off review's corrections into proposed observations. Without `apply`
 * it's a dry run returning the plan (`planned`/`skipped`); nothing is committed either way —
 * each observation still needs a human to commit it. */
export const promoteReview = (mapId: string, apply = false) =>
  api.post<{ planned?: unknown[]; created?: unknown[]; skipped: unknown[] }>(
    `/maps/${encodeURIComponent(mapId)}/review/promote`,
    {
      apply,
    },
  );

/** Signed-off maps only — an unreviewed map is never a delivery brief. */
export const listGuardrails = () => api.getCached<{ maps: GuardrailsSummary[] }>("/guardrails");
/** A signed-off map reconciled with its review (what the reviewer concluded), plus
 * staleness: what it references that changed since the map read the catalog. */
export const getGuardrails = (mapId: string) => api.get<ResolvedGuardrails>(`/guardrails/${encodeURIComponent(mapId)}`);
/** The same guardrails rendered as markdown, for a ticket or design doc. Fetched through the
 * API client (a plain link wouldn't carry the sign-in token, and the API would refuse it). */
export const getGuardrailsMarkdown = (mapId: string) =>
  api.get<string>(`/guardrails/${encodeURIComponent(mapId)}/markdown`);

/** Proposed registry changes (observations) with their evidence, and their outcome. */
export const listObservations = () => api.get<{ observations: Observation[] }>("/observations");
/** Applies an observation to the registry and reports what it changed. */
export const commitObservation = (id: string) =>
  api.post<{ committed_at: string; effect: string[] }>(`/observations/${encodeURIComponent(id)}/commit`, {});
/** Rejects an observation, with the reason. */
export const rejectObservation = (id: string, reason: string) =>
  api.post<{ id: string }>(`/observations/${encodeURIComponent(id)}/reject`, { reason });
